import { forwardRef, type ReactNode } from 'react';
import RwButton, { type RwButtonProps } from './RwButton';

/**
 * RwIconButton — icon-only DS button (rw-ds.css `.rwb--icon-only`). Same tiers/
 * sizes/states as RwButton; the square footprint = the size's height. Themed
 * identically to RwButton so the two never diverge (MUI-BRIDGE §Button). An
 * accessible name is required (`aria-label`) since there is no text.
 *
 * Default tier is `plain-neutral` (a quiet neutral icon action — the common
 * top-bar / toolbar case). Menu-trigger icon buttons get the open-state fill
 * automatically via `.rwb--icon-only[aria-expanded="true"]`.
 *
 * Props forward, including `className` — everything except `tier`/`children`
 * rides `...rest` into `RwButton`, which merges `className` into the `.rwb`
 * class list. `RwButtonProps` extends `ButtonHTMLAttributes<HTMLButtonElement>`,
 * so a bad prop name is a type error rather than a silent drop.
 */
export interface RwIconButtonProps extends Omit<RwButtonProps, 'iconOnly' | 'startIcon' | 'endIcon' | 'children'> {
  'aria-label': string;
  children: ReactNode;
  /**
   * Overrides the icon-only footprint WIDTH (the DS makes it a square: width = the size's height).
   * For a mark that is wider than tall, e.g. the 41.5x24 RW logo in a 49.5x32 button. Height stays
   * the size tier's. Applied as an inline `style` width on purpose: `rw-ds.css` sets
   * `.rwb--sm.rwb--icon-only { width }` (two classes), which beats a plain `sx` width (one emotion
   * class) in a real browser, and an inline declaration beats it whatever the stylesheet order.
   */
  footprintWidth?: string;
}

const RwIconButton = forwardRef<HTMLButtonElement, RwIconButtonProps>(function RwIconButton(
  { tier = 'plain-neutral', children, footprintWidth, style, ...rest },
  ref
) {
  return (
    <RwButton ref={ref} tier={tier} iconOnly style={footprintWidth ? { width: footprintWidth, ...style } : style} {...rest}>
      {children}
    </RwButton>
  );
});

export default RwIconButton;
